import { Control, Controller, FieldError } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import React, { HTMLInputTypeAttribute, useMemo, useState } from 'react';
import ValidationsType from '@ameroservices-platform/shared/ui-components/fields/ValidationsType';
import VariantType from '@ameroservices-platform/shared/ui-components/fields/VariantType';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Checkbox, CircularProgress, Icon, Tooltip } from '@mui/material';
import { hasPermission } from '@ameroservices-platform/shared/utility/index';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import InputAdornment from '@mui/material/InputAdornment';
import firebaseService from '@ameroservices-platform/shared/services/firebase';
import FlagIcon from '@ameroservices-platform/shared/ui-components/FlagIcon';
import SecretVisibilityToggle from '@ameroservices-platform/shared/ui-components/fields/SecretVisibilityToggle';

function TextFieldForm({
	name,
	label,
	control,
	className,
	type,
	onlyPositive,
	validations,
	variant,
	fullWidth,
	required,
	disabled,
	hideHelperText,
	noEmptyHelperText,
	InputProps,
	InputLabelProps,
	placeholder,
	helperText,
	size,
	multiline,
	onlyAmeroAdmin,
	autoFocus,
	sx,
	error: isError,
	onChange,
	hasLanguage,
	secret
}: {
	name: string;
	label: string;
	control: Control<any>;
	className?: string;
	type?: HTMLInputTypeAttribute | 'integer';
	onlyPositive?: boolean;
	validations?: ValidationsType;
	variant?: VariantType;
	fullWidth?: boolean;
	required?: boolean;
	disabled?: boolean;
	hideHelperText?: boolean;
	noEmptyHelperText?: boolean;
	helperText?: string;
	InputProps?: any;
	InputLabelProps?: any;
	placeholder?: string;
	size?: 'small' | 'medium';
	multiline?: boolean;
	onlyAmeroAdmin?: boolean;
	autoFocus?: boolean;
	sx?: any;
	error?: boolean;
	onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
	hasLanguage?: boolean;
	secret?: boolean;
}) {
	const [secretRevealed, setSecretRevealed] = useState(false);
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const user = useSelector(state => state.auth.user);
	const languages = useSelector(
		(state: any) =>
			(state?.cmsApp && state.cmsApp.languages) ||
			(state?.shared?.frontend && state.shared.frontend.languages) ||
			null
	) as Record<string, { deleted: boolean; flag: string; isoCode: string; title: string }> | null;
	const { t } = useTranslation();
	const userRoleIsAmeroAdmin = useMemo(() => hasPermission(['ameroAdmin'], user.role), [user.role]);
	const languageModuleEnabled = firebaseService.getOrganisationModule('language');
	const rules = useMemo(() => {
		if (disabled) {
			return {};
		}
		const rules: ValidationsType = _.cloneDeep(validations || {});
		if (required) {
			rules.required = t('FORM_REQUIRED_FIELD', 'Dette felt er påkrævet') as string;
		}
		if (type === 'number') {
			rules.pattern = {
				message: t('FORM_INVALID_NUMBER', 'Indtast et gyldigt tal'),
				value: /^[0-9][0-9]*[,.]{0,1}:*[0-9]{0,2}?$/
			};
		}
		if (type === 'integer') {
			if (onlyPositive) {
				rules.pattern = {
					message: t('FORM_POSITIVE_INTEGER_ONLY', 'Indtast et gyldigt positivt heltal'),
					value: /^\d*$/
				};
			} else {
				rules.pattern = {
					message: t('FORM_INTEGER_ONLY', 'Indtast et gyldigt heltal'),
					value: /^[-]*[0-9][0-9]*[,.]{0,1}:*[0-9]{0,2}?$/
				};
			}
		}
		if (rules.isEmail) {
			rules.pattern = {
				message: t('FORM_INVALID_EMAIL', 'Indtast en gyldige e-mailadresse'),
				value: /^(([^<>()[\]\\/.,;:\s@"]+(\.[^<>()[\]\\/.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i
			};
		}
		if (rules.equalsField) {
			const fieldToCompare = rules.equalsField;
			rules.validate = (value: string) => {
				const compareTo = control._formValues?.[fieldToCompare];
				return value === compareTo || String(t('CONFIRM_EMAIL_ERROR', 'De to e-mailadresser er ikke ens'));
			};
		}
		return rules;
	}, [validations, required, disabled, onlyPositive]);

	const getHelperText = (
		error: FieldError | undefined,
		hideHelperText: boolean | undefined,
		noEmptyHelperText: boolean | undefined
	) => {
		if (hideHelperText) {
			return null;
		}
		if (error && error.message) {
			return error.message;
		}
		if (helperText) {
			return helperText;
		}
		if (noEmptyHelperText) {
			return ' ';
		}
		return null;
	};

	if (onlyAmeroAdmin && !userRoleIsAmeroAdmin) {
		return null;
	}

	return (
		<div className={'w-full flex items-center gap-4'}>
			{onlyAmeroAdmin && (
				<Tooltip title={t('ONLY_FOR_AMERO_ADMINS', 'Kun muligt for amero admins') as string}>
					<Icon className={'mr-10'}>security</Icon>
				</Tooltip>
			)}

			<div className={'w-full flex flex-col gap-10'}>
				{languageModuleEnabled &&
				hasLanguage &&
				languages &&
				Object.keys(languages).length > 0 &&
				firebaseService.getOrganisationModule('language') ? (
					Object.values(languages)
						.sort((a, b) => {
							const aKey = a.isoCode;
							const bKey = b.isoCode;
							if (aKey < bKey) {
								return -1;
							}
							if (aKey > bKey) {
								return 1;
							}
							return 0;
						})
						.map(language => (
							<Controller
								name={`contentStrings.${name}.${language.isoCode}`}
								control={control}
								rules={language.isoCode === 'da' ? rules : _.omit(rules, ['required'])}
								render={({ field, fieldState: { invalid, error } }) => {
									return (
										<div
											key={`${name}_${language.isoCode}`}
											className="flex flex-row items-center w-full"
										>
											{language.isoCode !== 'da' && (
												<Tooltip title="Aktiver sprog">
													<Checkbox
														checked={typeof field.value === 'string'}
														onChange={() => {
															field.onChange(typeof field.value === 'string' ? null : '');
														}}
														disabled={disabled}
													/>
												</Tooltip>
											)}
											<div className="w-full">
												<TextField
													{...field}
													value={field.value || ''}
													error={invalid || !!error || !!isError}
													helperText={getHelperText(error, hideHelperText, noEmptyHelperText)}
													className={className}
													sx={sx}
													type={type === 'integer' ? 'number' : type || 'text'}
													name={`contentStrings.${name}.${language.isoCode}`}
													label={`${label} (${language.isoCode})`}
													variant={variant || 'outlined'}
													fullWidth={fullWidth}
													required={required && language.isoCode === 'da'}
													disabled={
														disabled ||
														(typeof field.value !== 'string' && language.isoCode !== 'da')
													}
													size={size}
													InputProps={{
														...(InputProps || {}),
														className: clsx(
															InputProps?.className,
															disabled ? 'bg-gray-100' : ''
														),
														endAdornment: (
															<InputAdornment position="end">
																<FlagIcon
																	flagCountryCode={language.flag}
																	className={undefined}
																/>
															</InputAdornment>
														)
													}}
													InputLabelProps={InputLabelProps}
													placeholder={placeholder}
													multiline={multiline}
													autoFocus={autoFocus}
													onChange={event => {
														field.onChange(event);
														if (onChange) {
															onChange(event);
														}
													}}
												/>
											</div>
										</div>
									);
								}}
							/>
						))
				) : (
					<Controller
						name={hasLanguage ? `contentStrings.${name}.da` : name}
						control={control}
						rules={rules}
						render={({ field, fieldState: { invalid, error } }) => {
							return (
								<TextField
									{...field}
									value={field.value || ''}
									error={invalid || !!error || !!isError}
									helperText={getHelperText(error, hideHelperText, noEmptyHelperText)}
									className={className}
									sx={sx}
									type={
										secret && !secretRevealed ? 'password' : type === 'integer' ? 'number' : type || 'text'
									}
									name={hasLanguage ? `contentStrings.${name}.da` : name}
									label={label}
									variant={variant || 'outlined'}
									fullWidth={fullWidth}
									required={required}
									disabled={disabled}
									size={size}
									autoComplete={secret ? 'off' : undefined}
									InputProps={{
										...(InputProps || {}),
										className: clsx(InputProps?.className, disabled ? 'bg-gray-100' : ''),
										...(secret
											? {
													endAdornment: (
														<SecretVisibilityToggle
															revealed={secretRevealed}
															onToggle={() => setSecretRevealed(r => !r)}
														/>
													)
											  }
											: {})
									}}
									InputLabelProps={InputLabelProps}
									placeholder={placeholder}
									multiline={multiline}
									autoFocus={autoFocus}
									onChange={event => {
										field.onChange(event);
										if (onChange) {
											onChange(event);
										}
									}}
								/>
							);
						}}
					/>
				)}
			</div>
		</div>
	);
}

export default TextFieldForm;
